import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import MultiLargeFileUploads from '../../components/modal/MultiLargeFileUploads';
import styles from './RNAContent.module.css';
import DEGAnalysisModal from './DEGAnalysisModal';
import RNAProcessedOutputFileListPopup from './RNAProcessedOutputFileListPopup';
import ReportModal from '../../components/modal/ReportModal'; // 임시용리포트
// 엑셀 생성 관련 라이브러리
import { utils, write } from 'xlsx';
import { saveAs } from 'file-saver';

// img import
import downloadIcon from '../../img/common/downloadIcon.png';
import uploadIcon from '../../img/icon/uploadIconOrange.png';
import upArrowGray from '../../img/icon/upArrowGray.png';
import downArrowGray from '../../img/icon/downArrowGray.png';
import filterImage from '../../img/common/filter.png';

const TABS = {
    0: 'Lung',
    1: 'Gastric',
    2: 'Colon',
    3: 'Pancreas',
    4: 'Ovary',
    // 5: 'Bladder',
    6: 'Breast',
};

function RNAContent({ initialSearch }) {
    const [isReportModalOpen, setIsReportModalOpen] = useState(false);
    const [selectedReportSample, setSelectedReportSample] = useState(null);
    // 리포트 팝업 열기 함수
    const handleOpenReport = (sampleName) => {
        setSelectedReportSample(sampleName);
        setIsReportModalOpen(true);
    };

    const { t } = useTranslation();
    const [mappingSummaryList, setMappingSummaryList] = useState([]);
    const [processedFiles, setProcessedFiles] = useState([]);
    const [filterText, setFilterText] = useState(initialSearch || '');
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [isDownloading, setIsDownloading] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const [userName, setUserName] = useState('');
    const [authLevel, setAuthLevel] = useState('');
    const [showProcessedOutput, setShowProcessedOutput] = useState(false);
    const [isAllSelected, setIsAllSelected] = useState(false);
    const [keywordFilters, setKeywordFilters] = useState({
        all: true,
        NT: false,
        TT: false,
        TO: false,
        NO: false,
    });
    // ✅ DEG 분석 모달 열기/닫기 상태
    const [showDEGModal, setShowDEGModal] = useState(false);
    // 검색어
    const [poSearch, setPoSearch] = useState('');

    // ESC 로 닫기
    useEffect(() => {
        if (!showProcessedOutput) return;
        const onKeyDown = (e) => {
            if (e.key === 'Escape') setShowProcessedOutput(false);
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [showProcessedOutput]);

    // ★ [추가] initialSearch가 있을 때 탭 자동 선택 로직
    useEffect(() => {
        if (initialSearch && initialSearch.length >= 3) {
            // NGS 코드의 3번째 글자 (인덱스 2) 추출하여 숫자로 변환
            // 예: G003016... -> '0' (인덱스 2) -> 0
            const tabKey = parseInt(initialSearch.charAt(2), 10);

            // 유효한 탭 번호인지 확인 (TABS 객체에 있는지)
            if (!isNaN(tabKey) && TABS[tabKey]) {
                setActiveTab(tabKey);
            }
        }
    }, [initialSearch]);

    // 페이지네이션 상태
    const [currentPage, setCurrentPage] = useState(1);
    const [currentPageInput, setCurrentPageInput] = useState('');
    const itemsPerPage = 15;

    // 정렬 상태: key는 정렬할 필드, order는 'desc', 'asc', 또는 null(원본 순서)
    const [sortConfig, setSortConfig] = useState({ key: null, order: null });

    // const itemsPerPage = 10;
    const [pageSize, setPageSize] = useState(15);


    // mapping summary 데이터는 마운트 시 한 번만 호출
    useEffect(() => {
        fetch('/api/getRNAMappingSummary', { method: 'GET' })
            .then(response => {
                if (!response.ok) {
                    throw new Error(`HTTP error, status = ${response.status}`);
                }
                return response.json();
            })
            .then(data => {
                if (data) {
                    setMappingSummaryList(data);
                }
            })
            .catch(error => console.log(error));
    }, []);

    useEffect(() => {
        const fetchAuthLevel = () => {
            fetch('/api/getAuthLevel', { method: 'POST' })
                .then(response => {
                    if (!response.ok) {
                        throw new Error(`HTTP error, status = ${response.status}`);
                    }
                    return response.json();
                })
                .then(data => {
                    if (data) {
                        setUserName(data.userName);
                        setAuthLevel(data.authLevel);
                    }
                })
                .catch(error => console.log(error));
        };
        fetchAuthLevel();
    }, []);

    const handleCheckboxChange = (sampleName) => {
        setSelectedFiles(prev =>
            prev.includes(sampleName)
                ? prev.filter(name => name !== sampleName)
                : [...prev, sampleName]
        );
    };

    const handleTabChange = (tabKey) => {
        setActiveTab(tabKey);
        setSelectedFiles([]);
        setIsAllSelected(false);
        setShowProcessedOutput(false);
        setCurrentPage(1);
    };

    // ★★ 클라이언트에서 엑셀 파일 생성하여 다운로드하는 함수 ★★
    const handleDownloadMappingSummary = (downloadType) => {
        let dataToDownload = [];
        if (downloadType === 'all') {
            // 현재 탭에 대한 전체 필터링된 데이터
            dataToDownload = filteredMappingSummaryList;
        } else if (downloadType === 'selected') {
            // 선택된 샘플에 해당하는 데이터 (필터링된 목록 기준)
            dataToDownload = filteredMappingSummaryList.filter(item =>
                selectedFiles.includes(item.sampleName)
            );
        }
        if (dataToDownload.length === 0) {
            alert(t('다운로드할 데이터가 없습니다.'));
            return;
        }

        // JSON 데이터를 엑셀 워크시트로 변환
        const worksheet = utils.json_to_sheet(dataToDownload);
        const workbook = utils.book_new();
        utils.book_append_sheet(workbook, worksheet, 'Sheet1');

        const currentDate = new Date().toISOString().slice(0, 10).replace(/-/g, '');
        const tabName = TABS[activeTab] || 'RNA';
        const fileName = `RNA_${tabName}_mappingsummary_${currentDate}.xlsx`;

        const excelBuffer = write(workbook, { bookType: 'xlsx', type: 'array' });
        const dataBlob = new Blob([excelBuffer], { type: 'application/octet-stream' });
        saveAs(dataBlob, fileName);
    };


    const handleKeywordFilterChange = (key) => {
        setKeywordFilters(prev => {
            const newFilters = { ...prev };
            if (key === 'all') {
                newFilters.all = true;
                newFilters.NT = false;
                newFilters.TT = false;
                newFilters.TO = false;
                newFilters.NO = false;
            } else {
                newFilters[key] = !prev[key];
                if (newFilters.NT || newFilters.TT || newFilters.TO || newFilters.NO) {
                    newFilters.all = false;
                } else {
                    newFilters.all = true;
                }
            }
            return newFilters;
        });
    };

    // 필터링: activeTab, filterText, keywordFilters에 따라 mappingSummaryList를 필터링
    // 그룹 분류 함수 (sampleName의 8번째 자리 기준)
    const getSampleGroup = (sampleName) => {
        if (!sampleName || sampleName.length < 8) return "";
        const groupCode = sampleName.substr(7, 2).toLowerCase();
        if (["nt", "nb", "np"].includes(groupCode)) return "NT";
        else if (["tt", "tf"].includes(groupCode)) return "TT";
        else if (["to", "te"].includes(groupCode)) return "TO";
        else if (groupCode === "no") return "NO";
        else if (sampleName.charAt(7).toLowerCase() === 't') return "TT"; // 그 외 T로 시작하면 TT로 분류
        else return "";
    };

    const filteredMappingSummaryList = useMemo(() => {
        // 우선, 활성화된 그룹 필터들을 모읍니다.
        const activeGroups = [];
        if (keywordFilters.NT) activeGroups.push("NT");
        if (keywordFilters.TT) activeGroups.push("TT");
        if (keywordFilters.TO) activeGroups.push("TO");
        if (keywordFilters.NO) activeGroups.push("NO");

        return mappingSummaryList.filter(item => {
            // 탭 필터: sampleName의 3번째 문자(0-indexed: charAt(2))가 activeTab과 동일해야 함
            const tabKey = parseInt(item.sampleName.charAt(2), 10);
            if (tabKey !== activeTab) return false;

            // 텍스트 필터
            const lowerFilterText = filterText.toLowerCase();
            // item.orderNumber가 null일 수 있으므로 기본값을 빈 문자열("")로 처리
            const orderNumberText = item.orderNumber ? item.orderNumber.toLowerCase() : "";
            if (
                !item.sampleName.toLowerCase().includes(lowerFilterText) &&
                !orderNumberText.includes(lowerFilterText)
            ) {
                return false;
            }

            // 전체보기(all) 필터가 활성화되어 있으면 추가 조건 없이 모두 포함
            if (keywordFilters.all) return true;

            // sampleName 기준 그룹 분류
            const group = getSampleGroup(item.sampleName);
            // 활성화된 그룹(activeGroups)에 해당 샘플의 그룹이 포함되어야 함.
            return activeGroups.includes(group);
        });
    }, [mappingSummaryList, activeTab, filterText, keywordFilters]);

    const handleSelectAll = () => {
        const filteredSamples = filteredMappingSummaryList.map(item => item.sampleName);

        if (isAllSelected) {
            setSelectedFiles(prev => prev.filter(name => !filteredSamples.includes(name)));
        } else {
            setSelectedFiles(prev => [...new Set([...prev, ...filteredSamples])]);
        }

        setIsAllSelected(!isAllSelected);
    };

    useEffect(() => {
        const filteredSamples = filteredMappingSummaryList.map(item => item.sampleName);
        const isAllFilteredSelected = filteredSamples.length > 0 && filteredSamples.every(sample => selectedFiles.includes(sample));

        setIsAllSelected(isAllFilteredSelected);
    }, [selectedFiles, filteredMappingSummaryList]);

    // 🔽 정렬 공통 유틸
    const isEmpty = (v) =>
        v === null || v === undefined || (typeof v === 'string' && v.trim() === '');

    const toNumberIfNumericLike = (v) => {
        if (typeof v === 'number') return v;
        if (typeof v === 'string') {
            // %, 콤마, 공백 제거 후 숫자 파싱
            const cleaned = v.replace(/[%,\s]/g, '');
            if (cleaned !== '' && !isNaN(cleaned)) return parseFloat(cleaned);
        }
        return null; // 숫자 아님
    };

    const compareWithNullLast = (a, b, order = 'asc') => {
        // 둘 다 비었으면 동일
        if (isEmpty(a) && isEmpty(b)) return 0;
        // 하나만 비었으면 빈값을 항상 뒤로
        if (isEmpty(a)) return 1;
        if (isEmpty(b)) return -1;

        // 숫자처럼 보이면 숫자로 비교
        const aNum = toNumberIfNumericLike(a);
        const bNum = toNumberIfNumericLike(b);
        if (aNum !== null && bNum !== null) {
            return order === 'asc' ? aNum - bNum : bNum - aNum;
        }

        // 문자열 비교 (대소문자 무시, 숫자 인식)
        const res = String(a).localeCompare(String(b), undefined, {
            numeric: true,
            sensitivity: 'base',
        });
        return order === 'asc' ? res : -res;
    };

    // 🔽 정렬: 모든 컬럼 공통 적용
    const sortedMappingSummaryList = useMemo(() => {
        const list = [...filteredMappingSummaryList];
        const { key, order } = sortConfig;
        if (!key || !order) return list;

        list.sort((rowA, rowB) => compareWithNullLast(rowA[key], rowB[key], order));
        return list;
    }, [filteredMappingSummaryList, sortConfig]);

    // 페이지네이션
    const totalPages = Math.ceil(sortedMappingSummaryList.length / pageSize);
    const indexOfLastItem = currentPage * pageSize;
    const indexOfFirstItem = indexOfLastItem - pageSize;
    const currentItems = sortedMappingSummaryList.slice(indexOfFirstItem, indexOfLastItem);

    // 필터링/정렬 후 totalPages가 줄어들면 currentPage를 보정
    useEffect(() => {
        // totalPages가 0일 땐 1페이지로 초기화
        const safePage = totalPages > 0 ? totalPages : 1;
        if (currentPage > safePage) {
            setCurrentPage(safePage);
        }
    }, [totalPages]);

    const handlePageChange = (newPage) => {
        if (newPage < 1 || newPage > totalPages) return;
        setCurrentPage(newPage);
    };

    const handlePageJump = () => {
        const newPage = parseInt(currentPageInput, 10);
        if (!isNaN(newPage) && newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
        } else {
            alert(t('유효한 페이지 번호를 입력하세요.'));
        }
    };

    // 정렬 핸들러: 같은 컬럼 클릭 시 내림차순 → 오름차순 → 원상복구
    const handleSort = (key) => {
        setSortConfig(prev => {
            if (prev.key !== key) {
                return { key, order: 'desc' };
            } else {
                if (prev.order === 'desc') return { key, order: 'asc' };
                if (prev.order === 'asc') return { key: null, order: null };
                return { key, order: 'desc' };
            }
        });
    };

    const totalMappingCount = mappingSummaryList.filter(item =>
        parseInt(item.sampleName.charAt(2), 10) === activeTab
    ).length;

    const selectedFilesCount = selectedFiles.length;
    const filteredFilesCount = filteredMappingSummaryList.length;

    const handleDownload = async () => {
        if (selectedFiles.length === 0) {
            alert(t('다운로드할 샘플을 선택하세요.'));
            return;
        }

        setIsDownloading(true);
        try {
            const response = await axios.post(
                '/api/download',
                { type: 'RNA', files: selectedFiles, activeTab: TABS[activeTab] },
                {
                    responseType: 'blob',
                    headers: { 'Content-Type': 'application/json' },
                }
            );

            if (!response.data || response.data.size === 0) {
                throw new Error('압축된 파일이 비어 있습니다.');
            }

            // ✅ 현재 탭명 가져오기
            const tabName = TABS[activeTab] || 'RNA';
            // ✅ 현재 날짜를 YYYYMMDD 형식으로 변환
            const currentDate = new Date().toISOString().slice(0, 10).replace(/-/g, '');
            // ✅ 파일명 생성
            const fileName = `${tabName}_gene_fusion_${currentDate}.zip`;

            // ✅ 파일 다운로드 처리
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error('Error downloading RNA files:', error);

            // ✅ 서버에서 보낸 에러 메시지 추출
            let errorMessage = t('네트워크 연결 상태를 확인하세요.');

            if (error.response) {
                // 서버가 응답을 보냈으나 4xx, 5xx 에러인 경우
                if (error.response.data instanceof Blob) {
                    // responseType이 'blob'일 경우 에러 메시지도 Blob으로 오므로 변환이 필요할 수 있음
                    errorMessage = `${t('데이터를 찾을 수 없거나 서버 오류가 발생했습니다.')} (Status: ${error.response.status})`;
                } else {
                    errorMessage = error.response.data.error || error.message;
                }
            } else {
                errorMessage = error.message;
            }

            // ✅ "Status code 404" 대신 실제 이유를 출력
            alert(`${t('파일 다운로드 중 오류가 발생했습니다:')} ${errorMessage}`);
        } finally {
            setIsDownloading(false);
        }
    };


    const handleProcessRNAExpression = async () => {
        if (selectedFiles.length === 0) {
            alert(t('처리할 샘플을 선택하세요.'));
            return;
        }
        const invalidFiles = selectedFiles.filter(sample => /TT|NT|TF/i.test(sample));
        if (invalidFiles.length > 0) {
            alert(`${t('다음 샘플에는 TT 또는 NT가 포함되어 처리할 수 없습니다:')}\n${invalidFiles.join(', ')}`);
            return;
        }
        setIsProcessing(true);
        try {
            const lungColonSamples = selectedFiles.filter(sample => {
                const tabKey = parseInt(sample.charAt(2), 10);
                return tabKey === 0 || tabKey === 2;
            });
            const otherSamples = selectedFiles.filter(sample => {
                const tabKey = parseInt(sample.charAt(2), 10);
                return tabKey !== 0 && tabKey !== 2;
            });

            // ✅ 현재 탭명 가져오기
            const tabName = TABS[activeTab] || 'RNA';
            // ✅ 현재 날짜를 YYYYMMDD 형식으로 변환
            const currentDate = new Date().toISOString().slice(0, 10).replace(/-/g, '');
            // ✅ 동적 파일명 생성
            const fileName = `${tabName}_gene_expression_${currentDate}.xlsx`;

            if (lungColonSamples.length > 0) {
                const response = await axios.post(
                    '/api/rna-expression/direct-fetch',
                    { samples: lungColonSamples, activeTab: TABS[activeTab] },
                    { responseType: 'blob' }
                );
                if (response.data) {
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', fileName); // ✅ 동적 파일명 적용
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                }
            }
            if (otherSamples.length > 0) {
                await axios.post('/api/rna-expression/process-and-fetch', {
                    samples: otherSamples,
                    userName: userName,
                });
                alert(t('RNA expression 처리 시작됨. 결과는 나중에 확인하세요.'));
            }
        } catch (error) {
            console.error('RNA expression 처리 실패:', error);
            alert(`${t('RNA expression 처리 중 오류가 발생했습니다:')} ${error.message}`);
        } finally {
            setIsProcessing(false);
        }
    };


    const handleShowProcessedOutput = async () => {
        if (!showProcessedOutput) {
            setShowProcessedOutput(!showProcessedOutput);
            try {
                const response = await axios.get('/api/files/?type=ProcessedOutput');
                setProcessedFiles(response.data.files || []);
                setShowProcessedOutput(true);
            } catch (error) {
                console.error('Error fetching processed files:', error);
                alert(t('Processed Output 파일 목록을 가져오는 중 오류가 발생했습니다.'));
            }
        } else {
            setShowProcessedOutput(!showProcessedOutput);
        }
    };

    // 정렬 헤더 셀(상단 커스텀 헤더에서 사용)
    const renderTopHeader = (label, key) => (
        <div
            type="button"
            className={styles.headerBtn}
            onClick={() => handleSort(key)}
            aria-pressed={sortConfig.key === key}
        >
            <span className={styles.headerFont}>{t(label)}</span>
            {sortConfig.key === key && (
                <span className={styles.sortChip} aria-hidden>
                    <img
                        src={sortConfig.order === 'asc' ? upArrowGray : downArrowGray}
                        alt=""
                        className={styles.sortIcon}
                    />
                </span>
            )}
        </div>
    );

    return (
        <div className={styles.rnaContent}>
            <div className={styles.RNAContentTitle}>{t('Summary List')} : RNA</div>
            <div className={styles.select}>
                <div className={styles.keywordFilters}>
                    <label className={`${styles.checkBoxLabel} ${keywordFilters.all ? styles.active : ''}`}>
                        <input
                            className={styles.inputCheckBox}
                            type="checkbox"
                            checked={keywordFilters.all}
                            onChange={() => handleKeywordFilterChange('all')}
                        />
                        {t('전체보기')}
                    </label>
                    <label className={`${styles.checkBoxLabel} ${keywordFilters.NT ? styles.active : ''}`}>
                        <input
                            className={styles.inputCheckBox}
                            type="checkbox"
                            checked={keywordFilters.NT}
                            onChange={() => handleKeywordFilterChange('NT')}
                        />
                        NT
                    </label>
                    <label className={`${styles.checkBoxLabel} ${keywordFilters.TT ? styles.active : ''}`}>
                        <input
                            className={styles.inputCheckBox}
                            type="checkbox"
                            checked={keywordFilters.TT}
                            onChange={() => handleKeywordFilterChange('TT')}
                        />
                        TT
                    </label>
                    <label className={`${styles.checkBoxLabel} ${keywordFilters.NO ? styles.active : ''}`}>
                        <input
                            className={styles.inputCheckBox}
                            type="checkbox"
                            checked={keywordFilters.NO}
                            onChange={() => handleKeywordFilterChange('NO')}
                        />
                        NO
                    </label>
                    <label className={`${styles.checkBoxLabel} ${keywordFilters.TO ? styles.active : ''}`}>
                        <input
                            className={styles.inputCheckBox}
                            type="checkbox"
                            checked={keywordFilters.TO}
                            onChange={() => handleKeywordFilterChange('TO')}
                        />
                        TO
                    </label>
                </div>
            </div>
            <div className={styles.rnaHeadGrid}>
                <div>
                    <div className={styles.smallTitle}>
                        {t('전체 샘플')}: {totalMappingCount}
                    </div>
                    <div className={styles.smallTitle}>
                        {t('선택된 샘플')}: {selectedFilesCount}
                    </div>
                </div>
                <div>
                    <input
                        type="text"
                        placeholder={t('샘플 이름 또는 수주번호로 필터링')}
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                        className={styles.filterInput}
                        style={{
                            backgroundImage: `url(${filterImage})`,
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: '15px center',
                            backgroundSize: 'auto 70%',
                            paddingLeft: '35px',
                        }}
                    />
                </div>
                <div>
                    <div onClick={() => handleDownloadMappingSummary('all')} className={styles.summaryDownloadButton}>
                        {t('전체 Mapping Summary 다운로드')}
                    </div>
                </div>
                <div>
                    <div onClick={() => handleDownloadMappingSummary('selected')} className={styles.summaryDownloadButton}>
                        {t('선택된 Mapping Summary 다운로드')}
                    </div>
                </div>
            </div>

            {/* 현재작업 */}
            <div className={styles.topLine} />
            <div className={styles.dataGrid}>
                <div className={styles.dataGridLeft}>
                    <div onClick={() => setShowUploadModal(true)} className={styles.uploadButton}>
                        <img src={uploadIcon} className={styles.uploadIcon} alt="upload" />
                        {t('파일 업로드')}
                    </div>
                    {Object.entries(TABS).map(([key, label]) => (
                        <div
                            key={key}
                            className={`${styles.tabButton} ${activeTab === parseInt(key, 10) ? styles.active : ''}`}
                            onClick={() => handleTabChange(parseInt(key, 10))}
                        >
                            {t(label)}
                        </div>
                    ))}
                </div>
                <div className={styles.dataGridMid} />
                <div className={styles.dataGridRight}>
                    <div className={styles.tableHeader}>
                        <div className={styles.centerCell}>
                            <div
                                className={`${styles.radioDot} ${isAllSelected ? styles.on : ''}`}
                                role="checkbox"
                                aria-checked={isAllSelected}
                                tabIndex={0}
                                onClick={handleSelectAll}
                                onKeyDown={(e) => {
                                    if (e.key === ' ' || e.key === 'Enter') {
                                        e.preventDefault();
                                        handleSelectAll();
                                    }
                                }}
                            />
                        </div>
                        {renderTopHeader('수주번호', 'orderNumber')}
                        {renderTopHeader('Sample', 'sampleName')}
                        {renderTopHeader('Status', 'status')}
                        {renderTopHeader('Total Read', 'totalRead')}
                        {renderTopHeader('Mapped Read', 'mappedRead')}
                        {renderTopHeader('Mapping Rate (%)', 'percent')}
                        {renderTopHeader('Expressed Gene', 'expressedGene')}
                        {renderTopHeader('Fusion Gene', 'fusionGene')}
                        <div className={styles.headerBtn}>
                            <span className={styles.headerFont}>{t('Report')}</span>
                        </div>
                    </div>
                    <div className={styles.tableHeaderBottom} />
                    <div className={styles.tableBody}>
                        {currentItems.map(item => {
                            const isSelected = selectedFiles.includes(item.sampleName);
                            return (
                                <div
                                    className={`${styles.tableContent} ${isSelected ? styles.selectedRow : ''}`}
                                    key={item.sampleName}
                                    onClick={() => handleCheckboxChange(item.sampleName)}
                                    role="row"
                                    aria-selected={isSelected}
                                >
                                    <div className={styles.centerCell}>
                                        <div
                                            className={`${styles.radioDot} ${isSelected ? styles.on : ''}`}
                                            role="checkbox"
                                            aria-checked={isSelected}
                                            aria-label={`${item.sampleName} ${t('선택')}`}
                                            tabIndex={0}
                                            onClick={(e) => { e.stopPropagation(); handleCheckboxChange(item.sampleName); }}
                                            onKeyDown={(e) => {
                                                if (e.key === ' ' || e.key === 'Enter') {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    handleCheckboxChange(item.sampleName);
                                                }
                                            }}
                                        />
                                    </div>
                                    <div className={styles.centerCell}>{item.orderNumber}</div>
                                    <div className={styles.centerCell}>{item.sampleName}</div>
                                    {/* <div className={styles.centerCell}>{item.status}</div> */}
                                    <div className={styles.centerCell} style={{ textAlign: 'center', lineHeight: '1.2' }}>
                                        {item.status && item.status.includes(' (') ? (
                                            <>
                                                {item.status.split(' (')[0]}
                                                <br />
                                                ({item.status.split(' (')[1]}
                                            </>
                                        ) : (
                                            item.status
                                        )}
                                    </div>
                                    <div className={styles.centerCell}>{item.totalRead != null ? Number(item.totalRead).toLocaleString() : ''}</div>
                                    <div className={styles.centerCell}>{item.mappedRead != null ? Number(item.mappedRead).toLocaleString() : ''}</div>
                                    <div className={styles.centerCell}>
                                        {item.percent != null && item.percent !== '' && !isNaN(Number(item.percent))
                                            ? Number(item.percent).toFixed(2)
                                            : '-'}
                                    </div>
                                    <div className={styles.centerCell}>{item.expressedGene != null ? Number(item.expressedGene).toLocaleString() : ''}</div>
                                    <div className={styles.centerCell}>{item.fusionGene != null ? Number(item.fusionGene).toLocaleString() : ''}</div>
                                    <div className={styles.centerCell}>
                                        <span
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleOpenReport(item.sampleName);
                                            }}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            🔍
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            <div className={styles.buttonGroup}>
                <div className={styles.mappingSummaryContainer}>
                    <div className={styles.paginationBar}>
                        {/* 맨 앞으로 */}
                        <button
                            type="button"
                            className={`${styles.pagerBtn} ${currentPage === 1 ? styles.pagerBtnDisabled : ''}`}
                            onClick={() => currentPage > 1 && setCurrentPage(1)}
                            disabled={currentPage === 1}
                            aria-label={t('첫 페이지')}
                            title={t('첫 페이지')}
                        >
                            «
                        </button>

                        {/* 한 페이지 뒤로 */}
                        <button
                            type="button"
                            className={`${styles.pagerBtn} ${currentPage === 1 ? styles.pagerBtnDisabled : ''}`}
                            onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
                            disabled={currentPage === 1}
                            aria-label={t('이전 페이지')}
                            title={t('이전 페이지')}
                        >
                            ‹
                        </button>

                        {/* 현재 페이지 / 전체 페이지 */}
                        <span className={styles.pageStat}>
                            {currentPage} / {totalPages}
                        </span>

                        {/* 한 페이지 앞으로 */}
                        <button
                            type="button"
                            className={`${styles.pagerBtn} ${currentPage === totalPages ? styles.pagerBtnDisabled : ''}`}
                            onClick={() => currentPage < totalPages && setCurrentPage(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            aria-label={t('다음 페이지')}
                            title={t('다음 페이지')}
                        >
                            ›
                        </button>

                        {/* 맨 뒤로 */}
                        <button
                            type="button"
                            className={`${styles.pagerBtn} ${currentPage === totalPages ? styles.pagerBtnDisabled : ''}`}
                            onClick={() => currentPage < totalPages && setCurrentPage(totalPages)}
                            disabled={currentPage === totalPages}
                            aria-label={t('마지막 페이지')}
                            title={t('마지막 페이지')}
                        >
                            »
                        </button>

                        {/* 점프 입력창: "페이지번호 + Enter" */}
                        <input
                            type="number"
                            className={styles.pageJumpInput}
                            placeholder={t('페이지 번호 + Enter')}
                            value={currentPageInput}
                            onChange={(e) => setCurrentPageInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    const v = parseInt(currentPageInput, 10);
                                    if (!isNaN(v)) {
                                        const clamped = Math.min(Math.max(v, 1), totalPages || 1);
                                        setCurrentPage(clamped);
                                    }
                                }
                            }}
                            aria-label={t('페이지 번호 입력')}
                            title={t('페이지 번호 입력 후 Enter')}
                        />
                    </div>
                </div>

                <div className={styles.analysisGroup}>
                    <button
                        onClick={handleProcessRNAExpression}
                        disabled={isProcessing || isDownloading}
                        className={isProcessing ? styles.disabledButton : ''}
                    >
                        {isProcessing ? t('RNA expression 처리 중...') : t('Expression 분석')}
                    </button>
                    <button
                        onClick={handleDownload}
                        disabled={isDownloading || isProcessing}
                        className={isDownloading ? styles.disabledButton : ''}
                    >
                        {isDownloading ? t('다운로드 중...') : t('Fusion 분석')}
                    </button>
                    <button
                        type="button"
                        onClick={() => setShowDEGModal(true)}
                        disabled={isDownloading || isProcessing}
                    >
                        {t('DEG 분석')}
                    </button>
                    <button
                        className={styles.processedOutputButton}
                        onClick={handleShowProcessedOutput}
                    >
                        {t(showProcessedOutput ? '분석 결과 리스트 닫기' : '분석 결과 리스트 보기')}
                    </button>
                </div>
            </div>
            {showProcessedOutput && (
                <RNAProcessedOutputFileListPopup
                    onClose={() => setShowProcessedOutput(false)}
                    processedFiles={processedFiles}
                    activeTab={activeTab}
                    TABS={TABS}
                    t={t}
                />
            )}

            {showUploadModal && (
                <MultiLargeFileUploads onClose={() => setShowUploadModal(false)} type={'RNA'} />
            )}
            {showDEGModal && (
                <DEGAnalysisModal
                    onClose={() => setShowDEGModal(false)}
                    selectedFiles={selectedFiles}
                    activeTab={activeTab}
                    rnaList={mappingSummaryList}
                    userName={userName}
                />
            )}
            {/* RNAContent.js return 문 제일 끝부분, 기존 모달들 있는 곳에 추가 */}
            <ReportModal
                isOpen={isReportModalOpen}
                onClose={() => setIsReportModalOpen(false)}
                sampleName={selectedReportSample}
                reportType="RNA"
            />
        </div>
    );
}

export default RNAContent;